import type { Audience, Category, DocumentType, HistoryEntry, Template } from './api'

/** Rótulos por extenso. O valor do enum nunca aparece cru na tela. */

export const AUDIENCE_LABELS: Record<Audience, string> = {
  patient: 'Pacientes',
  student: 'Alunos',
  employee: 'Funcionários',
  third_party: 'Terceiros',
}

export const CATEGORY_LABELS: Record<Category, string> = {
  service_contract: 'Contrato de prestação de serviços',
  model_patient_contract: 'Contrato de paciente modelo',
  informed_consent: 'Termo de consentimento',
  anamnesis: 'Anamnese',
  standalone_form: 'Termo avulso',
  refund: 'Devolução',
  general_contract: 'Contrato',
  other: 'Outro documento',
}

/** O tipo do dialeto que cada categoria exige (docs/areas/documentos-e-assinaturas.md, RN-DOC-02). */
export const CATEGORY_TYPE: Record<Category, DocumentType> = {
  service_contract: 'contract',
  model_patient_contract: 'contract',
  informed_consent: 'consent_form',
  anamnesis: 'anamnesis',
  standalone_form: 'standalone',
  refund: 'standalone',
  general_contract: 'contract',
  other: 'other',
}

/** As categorias que cada público aceita (RN-DOC-42). */
export const AUDIENCE_CATEGORIES: Record<Audience, Category[]> = {
  patient: [
    'informed_consent',
    'service_contract',
    'model_patient_contract',
    'anamnesis',
    'standalone_form',
    'refund',
    'general_contract',
    'other',
  ],
  student: ['general_contract', 'standalone_form', 'other'],
  employee: ['general_contract', 'standalone_form', 'other'],
  third_party: ['general_contract', 'standalone_form', 'other'],
}

/** Só termo de consentimento e termo avulso de Pacientes acompanham procedimento (RN-ADM-73). */
export function canLinkProcedures(template: Pick<Template, 'audience' | 'category'>) {
  return (
    template.audience === 'patient' &&
    (template.category === 'informed_consent' || template.category === 'standalone_form')
  )
}

export type TemplateStatus = {
  label: string
  level: 'attention' | 'waiting' | 'done'
}

/**
 * O estado do documento na lista: o que pede ação da Westhetics vem como `attention`. A versão publicada pela carga
 * do sistema anterior sem revisão jurídica vale nas clínicas, mas não está pronta (RN-DOC-53).
 */
export function templateStatus(
  template: Pick<
    Template,
    'state' | 'hasUnpublishedChanges' | 'currentVersion' | 'isLegalReviewPending'
  >,
): TemplateStatus {
  if (template.state === 'archived') return { label: 'Arquivado', level: 'waiting' }
  if (!template.currentVersion) return { label: 'Rascunho', level: 'waiting' }
  if (template.isLegalReviewPending) return { label: 'Sem revisão jurídica', level: 'attention' }
  if (template.hasUnpublishedChanges)
    return { label: 'Alterações por publicar', level: 'attention' }
  return { label: 'Publicado', level: 'done' }
}

/**
 * "do sistema anterior · 212 envios em 90 dias · 14 clínicas": de onde o documento veio e quanto era usado, para o
 * jurídico saber por onde começar. Nulo no documento escrito na Westhetics.
 */
export function legacyUsage(template: Pick<Template, 'legacy'>): string | null {
  const legacy = template.legacy
  if (!legacy) return null
  const parts = ['do sistema anterior']
  if (legacy.sendsLast90Days !== null) {
    parts.push(
      legacy.sendsLast90Days === 1
        ? '1 envio em 90 dias'
        : `${legacy.sendsLast90Days} envios em 90 dias`,
    )
  }
  if (legacy.activeClinics !== null) {
    parts.push(legacy.activeClinics === 1 ? '1 clínica' : `${legacy.activeClinics} clínicas`)
  }
  return parts.join(' · ')
}

const ACTION_LABELS: Record<HistoryEntry['action'], string> = {
  created: 'Documento criado',
  updated: 'Dados alterados',
  draft_updated: 'Rascunho alterado',
  draft_discarded: 'Alterações descartadas',
  published: 'Versão publicada',
  linked: 'Vinculado a procedimento',
  unlinked: 'Desvinculado de procedimento',
  archived: 'Arquivado',
  restored: 'Restaurado',
}

export function historyActionLabel(action: HistoryEntry['action']) {
  return ACTION_LABELS[action] ?? action
}

/** "2026.10": ano e mês da publicação, o rótulo sugerido da versão (RN-DOC-47). */
export function suggestVersionLabel(taken: readonly string[], now: Date = new Date()) {
  const base = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}`
  if (!taken.includes(base)) return base
  let suffix = 2
  while (taken.includes(`${base}-${suffix}`)) suffix++
  return `${base}-${suffix}`
}
